import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "@/context/LanguageContext";
import { ABOUT_NAV } from "@/data/about";

const LINKS = [
  { to: "/", key: "home", testid: "nav-link-home" },
  { to: "/chi-siamo", key: "about", testid: "nav-link-about" },
  { to: "/adv", key: "adv", testid: "nav-link-adv" },
  { to: "/digital", key: "digital", testid: "nav-link-digital" },
  { to: "/corporate", key: "corporate", testid: "nav-link-corporate" },
  { to: "/ai", key: "ai", testid: "nav-link-ai" },
  { to: "/video-clip", key: "videoclip", testid: "nav-link-videoclip" },
  { to: "/news", key: "news", testid: "nav-link-news" },
  { to: "/contatti", key: "contact", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const label = (key) => (key === "about" ? ABOUT_NAV[lang] : key === "news" ? "News" : t.nav[key]);

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#08080A]/80 border-b border-white/10" data-testid="main-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/" aria-label="RV FILM home">
            <Logo />
          </Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Main">
            {LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `link-underline f-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    isActive ? "text-slate-50 active" : "text-slate-400 hover:text-slate-100"
                  }`
                }
              >
                {label(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <div className="flex items-center gap-1 f-mono text-[11px] tracking-[0.15em]" data-testid="language-switcher">
              <button
                data-testid="language-switcher-it"
                onClick={() => setLang("it")}
                className={`px-2 py-1 transition-colors ${lang === "it" ? "text-red-500" : "text-slate-500 hover:text-slate-300"}`}
              >
                IT
              </button>
              <span className="text-slate-700">/</span>
              <button
                data-testid="language-switcher-en"
                onClick={() => setLang("en")}
                className={`px-2 py-1 transition-colors ${lang === "en" ? "text-red-500" : "text-slate-500 hover:text-slate-300"}`}
              >
                EN
              </button>
            </div>
            <Link
              to="/contatti"
              data-testid="header-cta-button"
              className="group flex items-center gap-2 bg-red-600 hover:bg-red-500 transition-colors duration-300 text-white f-mono text-[11px] uppercase tracking-[0.15em] px-5 py-3 rounded-full"
            >
              {t.nav.cta}
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            className="lg:hidden text-slate-200 p-2"
            onClick={() => setOpen(true)}
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[98] bg-[#08080A]/98 backdrop-blur-2xl flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
              <Logo />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="text-slate-200 p-2">
                <X size={26} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2 overflow-y-auto py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.key}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                    className={`f-display block text-3xl font-extrabold tracking-tight py-2.5 ${
                      location.pathname === l.to ? "text-red-500" : "text-slate-50"
                    }`}
                  >
                    {label(l.key)}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10 flex items-center justify-between">
              <div className="flex items-center gap-2 f-mono text-xs tracking-[0.15em]">
                <button onClick={() => setLang("it")} data-testid="mobile-language-switcher-it" className={lang === "it" ? "text-red-500" : "text-slate-500"}>IT</button>
                <span className="text-slate-700">/</span>
                <button onClick={() => setLang("en")} data-testid="mobile-language-switcher-en" className={lang === "en" ? "text-red-500" : "text-slate-500"}>EN</button>
              </div>
              <Link
                to="/contatti"
                onClick={() => setOpen(false)}
                data-testid="mobile-header-cta-button"
                className="bg-red-600 text-white f-mono text-[11px] uppercase tracking-[0.15em] px-5 py-3 rounded-full"
              >
                {t.nav.cta}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
